export type NodeCategory = "trigger" | "action" | "delay" | "branch";

export interface WorkflowNodeData {
  category: NodeCategory;
  type: string;
  actionTypeId?: string;
  label: string;
  subtitle?: string;
  raw: Record<string, unknown>;
}

export interface WorkflowGraphNode {
  id: string;
  type: NodeCategory;
  data: WorkflowNodeData;
  position: { x: number; y: number };
}

export interface WorkflowGraphEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
}

interface ListBranch {
  branchName?: string;
  connection?: { nextActionId?: string };
}

interface FlowAction {
  actionId: string;
  type?: string;
  actionTypeId?: string;
  connection?: { nextActionId?: string };
  listBranches?: ListBranch[];
  fields?: Record<string, unknown>;
  [key: string]: unknown;
}

interface FlowDefinition {
  startActionId?: string;
  actions?: FlowAction[];
  enrollmentCriteria?: Record<string, unknown>;
  name?: string;
}

const TRIGGER_ID = "__trigger__";

const ACTION_TYPE_LABELS: Record<string, string> = {
  "0-1": "Delay",
  "0-2": "Schedule",
  "0-3": "Create task",
  "0-4": "Send internal email notification",
  "0-5": "Set property",
  "0-6": "Trigger webhook",
  "0-7": "Send automated email",
  "0-8": "Add to static list",
  "0-9": "Remove from static list",
  "0-10": "Set marketing contact status",
  "0-11": "Manage subscription preferences",
  "0-12": "Send in-app notification",
  "0-13": "Send marketing email",
  "0-14": "Create record",
  "0-15": "Communicate via Slack",
  "0-16": "Copy property value",
  "0-17": "Send Slack notification",
  "0-18": "Send SMS",
  "0-22": "Rotate record to owner",
  "0-23": "Add to ads audience",
  "0-24": "Remove from ads audience",
  "0-26": "Enroll in sequence",
  "0-27": "Unenroll from sequence",
  "0-32": "Copy property between objects",
  "0-35": "Create association",
  "0-43": "Send to Microsoft Teams",
};

function categorise(action: FlowAction): NodeCategory {
  if (action.type === "LIST_BRANCH") return "branch";
  if (action.actionTypeId === "0-1") return "delay";
  return "action";
}

function actionLabel(action: FlowAction): string {
  if (action.type === "LIST_BRANCH") return "If/then branch";
  if (action.actionTypeId && ACTION_TYPE_LABELS[action.actionTypeId]) {
    return ACTION_TYPE_LABELS[action.actionTypeId];
  }
  if (action.actionTypeId) return `Action ${action.actionTypeId}`;
  return action.type ?? "Action";
}

function actionSubtitle(action: FlowAction): string | undefined {
  const fields = action.fields;
  if (fields && typeof fields === "object") {
    const propertyName = (fields as { property_name?: unknown }).property_name;
    if (typeof propertyName === "string") return `Property: ${propertyName}`;
  }
  return undefined;
}

export function transformWorkflow(def: FlowDefinition): {
  nodes: WorkflowGraphNode[];
  edges: WorkflowGraphEdge[];
} {
  const actions = def.actions ?? [];
  const knownIds = new Set(actions.map((a) => a.actionId));
  const nodes: WorkflowGraphNode[] = [];
  const edges: WorkflowGraphEdge[] = [];

  nodes.push({
    id: TRIGGER_ID,
    type: "trigger",
    position: { x: 0, y: 0 },
    data: {
      category: "trigger",
      type: "ENROLLMENT",
      label: "Enrollment trigger",
      subtitle: def.name,
      raw: def.enrollmentCriteria ?? {},
    },
  });

  if (def.startActionId && knownIds.has(def.startActionId)) {
    edges.push({
      id: `${TRIGGER_ID}->${def.startActionId}`,
      source: TRIGGER_ID,
      target: def.startActionId,
    });
  }

  for (const action of actions) {
    const id = action.actionId;
    const category = categorise(action);
    nodes.push({
      id,
      type: category,
      position: { x: 0, y: 0 },
      data: {
        category,
        type: action.type ?? "UNKNOWN",
        actionTypeId: action.actionTypeId,
        label: actionLabel(action),
        subtitle: actionSubtitle(action),
        raw: action as unknown as Record<string, unknown>,
      },
    });

    if (action.type === "LIST_BRANCH") {
      const branches = action.listBranches ?? [];
      for (let i = 0; i < branches.length; i++) {
        const b = branches[i];
        const target = b.connection?.nextActionId;
        if (!target || !knownIds.has(target)) continue;
        edges.push({
          id: `${id}->${target}::${i}`,
          source: id,
          target,
          label: b.branchName,
        });
      }
      continue;
    }

    const next = action.connection?.nextActionId;
    if (next && knownIds.has(next)) {
      edges.push({
        id: `${id}->${next}`,
        source: id,
        target: next,
      });
    }
  }

  return { nodes, edges };
}
